import React, { memo, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormAutocompleteField from 'components/ui/form/FormAutocompleteField';
import FormSwitchField from 'components/ui/form/FormSwitchField';
import FormTextField from 'components/ui/form/FormTextField';
import useFormSubmit from 'hooks/useFormSubmit';

const validationSchema = yup.object({
  name: yup
    .string()
    .min(4, { label: 'validations.type.nameMin', value: 4 })
    .required('validations.type.name'),
  user_id: yup.number().required('validations.select.staff'),
});

const AddEditModal = ({ open, setOpen, setRefetch, itemData, setItemData }) => {
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      name: '',
      user_id: '',
      status: 0,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (itemData?.id) {
        submit(
          { type: 'postUpdate', contentType: 'simple' },
          values,
          '/admin/warehouses',
          values.name,
          itemData?.id,
          false,
          handleFinishRequest
        );
      } else
        submit(
          { type: 'post', contentType: 'simple' },
          values,
          '/admin/warehouses',
          values.name,
          null,
          false,
          handleFinishRequest
        );
    },
  });

  useEffect(() => {
    if (itemData?.id)
      formik.setValues({
        name: itemData?.name || '',
        user_id: itemData?.user_id || '',
        status: itemData?.status || 0,
      });
  }, [itemData]);

  const handleFinishRequest = () => {
    setRefetch(true);
    handleClose();
  };

  const handleClose = () => {
    setItemData('');
    setOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='md'
      PaperProps={{
        className: '!base-border !shadow-md',
      }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!bg-gray-100 !text-center !mb-2'
      >
        {itemData ? (
          <span>{t('warehouses.editTitle')}</span>
        ) : (
          <span>{t('warehouses.addTitle')}</span>
        )}
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3, lg: 3 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12, lg: 12 }}
          >
            <Grid item={true} lg={6} sm={6} xs={12}>
              <FormTextField
                delay={0.1}
                label={t('common.fields.simpleName')}
                fieldName='name'
                formik={formik}
              />
            </Grid>

            <Grid item={true} lg={6} sm={6} xs={12}>
              <FormAutocompleteField
                delay={0.4}
                label={t('common.fields.staff')}
                fieldName='user_id'
                formik={formik}
                autocomplete='off'
                path={'/dictionary/staffs'}
              />
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormSwitchField
                delay={0.5}
                label={t('common.fields.status')}
                fieldName='status'
                formik={formik}
                options={[
                  {
                    value: '1',
                    checked: true,
                  },
                  {
                    value: '0',
                    checked: false,
                  },
                ]}
              />
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormActionButtons
                delay={0.6}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(AddEditModal);
